import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '';
axios.defaults.baseURL = API_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('es_token'));
  const [loading, setLoading] = useState(true);

  // Session C (audit H3): tokens expire after 7 days and installed-PWA staff
  // keep sessions open longer than that. Without this, mid-session expiry made
  // every call silently 401 while the UI just looked broken. One interceptor
  // drops the user cleanly at the login screen instead. The login request
  // itself is excluded — a wrong password is a 401 too and must not "logout"
  // (which would wipe the error message mid-render).
  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        const url = err.config?.url || '';
        if (err.response?.status === 401 && !url.includes('/api/auth/login')) {
          logout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Only fetch /me on initial app load (when user is not yet set)
      // Login sets user directly so we skip the fetch in that case
      if (!user) {
        axios.get('/api/staff/me')
          .then(r => { setUser(r.data); setLoading(false); })
          .catch(() => { logout(); setLoading(false); });
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const { data } = await axios.post('/api/auth/login', { email, password });
    localStorage.setItem('es_token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    // Fetch full user profile so all fields (agency_id, status, etc.) are present
    const me = await axios.get('/api/staff/me');
    setToken(data.token);
    setUser(me.data);
    return me.data;
  };

  // Re-fetch the logged-in user's profile from the server.
  // The `user` object is cached in state from login/app-load and never updates
  // on its own — so anything an agent changes mid-session (skills, status,
  // personal_rate) stays stale in the UI while server-side queries see the new
  // values. Pages that render off those fields call this on mount.
  // useCallback keeps the identity stable so it's safe in a useEffect dep array.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/staff/me');
      setUser(data);
      return data;
    } catch {
      return null; // token invalid/expired — leave existing state alone
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('es_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
